import { BACKEND_URL } from "@/lib/backendUrl";
import { useCallback, useEffect, useState } from "react";
import { Download, FileCheck2, Loader2, PenLine } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
  DialogTitle } from "@/components/ui/dialog";
import { Chip, Empty, Kpi, Loading, Panel, Table, Td, Th } from "@/components/common/Chrome";
import { api, apiError } from "@/lib/api";
import { money, shortDate } from "@/lib/format";

const PERIODS = [["30d", "Last 30 days"], ["90d", "Last 90 days"], ["365d", "Last 12 months"],
  ["ytd", "This year so far"]];

const Row = ({ label, value, sub, strong, testId }) => (
  <div className="flex items-center justify-between border-b border-border/60 py-1.5"
       data-testid={testId}>
    <span className={`text-[12.5px] ${strong ? "font-semibold text-foreground"
      : "text-muted-foreground"}`}>{label}</span>
    <span className="flex items-baseline gap-2">
      {sub && <span className="font-mono text-[10.5px] text-muted-foreground">{sub}</span>}
      <span className={`font-mono text-[12px] ${strong ? "font-semibold text-foreground"
        : "text-foreground"}`}>{value}</span>
    </span>
  </div>
);

export const CostInsight = ({ currency = "INR" }) => {
  const [period, setPeriod] = useState("90d");
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    setData(null);
    try {
      const res = await api.get(`/cafeteria/cost-insight?period=${period}`);
      setData(res.data);
    } catch (e) { toast.error(apiError(e)); }
  }, [period]);

  useEffect(() => { load(); }, [load]);

  if (!data) return <Loading label="Costing the kitchen" />;

  const cur = data.currency || currency;
  const maxCost = Math.max(...data.trend.map((t) => t.cost), 1);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi testId="cost-per-head" label="Fully loaded per head"
             value={money(data.per_head.fully_loaded, cur)}
             sub={`${money(data.per_head.direct, cur)} food · ${money(data.per_head.labour, cur)} labour`} />
        <Kpi testId="cost-recovered" label="Recovered from diners" accent="teal"
             value={money(data.per_head.takings, cur)}
             sub={`${data.subsidy.recovery_percent}% of the cost`} />
        <Kpi testId="cost-subsidy" label="Subsidy per head"
             accent={data.subsidy.per_head > 0 ? "amber" : "emerald"}
             value={money(data.subsidy.per_head, cur)}
             sub={`${money(data.subsidy.total, cur, true)} over the period`} />
        <Kpi testId="cost-covers" label="Meals served" value={data.covers.meals_served}
             sub={`${data.covers.menus_served} menus · ${data.covers.paying_covers} paid`} />
      </div>

      <Panel title="What a meal costs" testId="panel-cost-layers"
             kicker="Food, then labour, then a share of the overheads"
             actions={(
               <div className="flex items-center gap-2">
                 <Select value={period} onValueChange={setPeriod}>
                   <SelectTrigger className="h-9 w-[160px]" data-testid="cost-period">
                     <SelectValue /></SelectTrigger>
                   <SelectContent>
                     {PERIODS.map(([key, label]) => (
                       <SelectItem key={key} value={key}>{label}</SelectItem>
                     ))}
                   </SelectContent>
                 </Select>
                 <a href={`${BACKEND_URL}/api/cafeteria/cost-insight?period=${period}`}
                    target="_blank" rel="noreferrer" data-testid="cost-raw"
                    className="text-[11.5px] text-muted-foreground underline">figures</a>
               </div>
             )}>
        <div className="grid gap-4 lg:grid-cols-2">
          <div data-testid="cost-layer-rows">
            <Row label="Ingredients" testId="cost-row-ingredients"
                 sub={`${money(data.per_head.ingredients, cur)}/head`}
                 value={money(data.cost.ingredients, cur)} />
            <Row label="Kitchen labour" testId="cost-row-labour"
                 sub={`${money(data.per_head.labour, cur)}/head`}
                 value={money(data.cost.labour, cur)} />
            <Row label="Overheads" testId="cost-row-overheads"
                 sub={`${money(data.per_head.overheads, cur)}/head`}
                 value={money(data.cost.overheads, cur)} />
            <Row label="Direct food cost" testId="cost-row-direct"
                 value={money(data.cost.direct, cur)} />
            <Row label="With labour" testId="cost-row-with-labour"
                 value={money(data.cost.with_labour, cur)} />
            <Row label="Fully loaded" strong testId="cost-row-fully-loaded"
                 value={money(data.cost.fully_loaded, cur)} />
            <Row label="Takings" testId="cost-row-takings" value={money(data.income.takings, cur)} />
            <Row label="Subsidy the charity carries" strong testId="cost-row-subsidy"
                 value={money(data.subsidy.total, cur)} />
          </div>
          <div className="space-y-2">
            <p className="text-[11px] uppercase tracking-[1.4px] text-muted-foreground">
              Kitchen hours
            </p>
            <div className="grid grid-cols-3 gap-2">
              {[["Staff", data.labour_hours.staff], ["Volunteer", data.labour_hours.volunteer],
                ["Not costed", data.labour_hours.uncosted]].map(([label, hours]) => (
                <div key={label} className="rounded-xl border border-border p-2.5">
                  <div className="text-[10.5px] uppercase tracking-[1.2px] text-muted-foreground">
                    {label}
                  </div>
                  <div className="font-display text-lg font-bold text-foreground">{hours}h</div>
                </div>
              ))}
            </div>
            <p className="pt-2 text-[11px] uppercase tracking-[1.4px] text-muted-foreground">
              How this is worked out
            </p>
            <ul className="space-y-1" data-testid="cost-assumptions">
              {data.assumptions.map((a) => (
                <li key={a} className="text-[11.5px] leading-relaxed text-muted-foreground">— {a}</li>
              ))}
            </ul>
          </div>
        </div>
      </Panel>

      <div className="grid gap-4 lg:grid-cols-2">
        <Panel title="Cost against takings" testId="panel-cost-trend"
               kicker={`By ${data.window.grain} · ${shortDate(data.window.from)} to ${shortDate(data.window.to)}`}>
          {data.trend.length === 0 ? <Empty label="Nothing served in this window" /> : (
            <div className="space-y-2.5" data-testid="cost-trend">
              {data.trend.map((t) => (
                <div key={t.label} data-testid={`cost-trend-${t.label}`}>
                  <div className="mb-1 flex items-center justify-between">
                    <span className="text-[12px] text-foreground">{t.label}</span>
                    <span className="font-mono text-[11px] text-muted-foreground">
                      {t.covers} meals · {money(t.cost, cur, true)} cost ·
                      {" "}{money(t.takings, cur, true)} taken
                    </span>
                  </div>
                  <div className="flex h-2 w-full overflow-hidden rounded-full bg-muted">
                    <div className="h-full bg-primary"
                         style={{ width: `${Math.min((t.takings / maxCost) * 100, 100)}%` }} />
                    <div className="h-full bg-amber-500/70"
                         style={{ width: `${Math.min((Math.max(t.subsidy, 0) / maxCost) * 100, 100)}%` }} />
                  </div>
                </div>
              ))}
              <p className="pt-1 text-[11px] text-muted-foreground">
                Indigo is recovered from diners, amber is the subsidy.
              </p>
            </div>
          )}
        </Panel>

        <Panel title="Dearest dishes per head" testId="panel-cost-dishes"
               kicker="Recipe cost against the price on the menu">
          <Table testId="cost-dish-table" head={<><Th>Dish</Th><Th>Cost/head</Th><Th>Price</Th>
            <Th>Margin</Th><Th>Served</Th></>}>
            {data.dishes.length === 0 && <Empty label="No dishes served yet" colSpan={5} />}
            {data.dishes.map((d) => (
              <tr key={d.dish_ref} className="border-b border-border/60"
                  data-testid={`cost-dish-${d.dish_ref}`}>
                <Td>{d.name}</Td>
                <Td className="font-mono text-[11px]">{money(d.cost_per_head, cur)}</Td>
                <Td className="font-mono text-[11px]">{money(d.price, cur)}</Td>
                <Td>
                  <Chip className={d.price - d.cost_per_head >= 0
                    ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
                    : "border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300"}>
                    {money(d.price - d.cost_per_head, cur)}
                  </Chip>
                </Td>
                <Td className="font-mono text-[11px]">{d.times_served}× · {d.covers}</Td>
              </tr>
            ))}
          </Table>
        </Panel>
      </div>

      <PriceAdvice period={period} currency={cur} />
    </div>
  );
};

const PriceAdvice = ({ period, currency }) => {
  const [data, setData] = useState(null);
  const [basis, setBasis] = useState("");
  const [recovery, setRecovery] = useState("");

  const load = useCallback(async () => {
    try {
      const q = new URLSearchParams({ period });
      if (basis) q.set("basis", basis);
      if (recovery !== "") q.set("recovery", recovery);
      const res = await api.get(`/cafeteria/price-advice?${q.toString()}`);
      setData(res.data);
      if (!basis) setBasis(res.data.basis);
      if (recovery === "") setRecovery(String(res.data.recovery_percent));
    } catch (e) { toast.error(apiError(e)); }
  }, [period, basis, recovery]);

  useEffect(() => { load(); }, [load]);

  if (!data) return <Loading label="Working out a fair price" />;
  const cur = data.currency || currency;

  return (
    <Panel title="Meal price advice" testId="panel-price-advice"
           kicker={`Priced off the actual cost · recovering ${data.recovery_percent}% of ${data.basis_label.toLowerCase()} · rounded up to the nearest ${data.rounding}`}
           actions={(
             <div className="flex items-center gap-2">
               <Select value={basis} onValueChange={setBasis}>
                 <SelectTrigger className="h-9 w-[260px]" data-testid="price-basis">
                   <SelectValue /></SelectTrigger>
                 <SelectContent>
                   {Object.entries(data.bases).map(([key, label]) => (
                     <SelectItem key={key} value={key}>{label}</SelectItem>
                   ))}
                 </SelectContent>
               </Select>
               <Input type="number" className="h-9 w-[86px]" data-testid="price-recovery"
                      value={recovery} onChange={(e) => setRecovery(e.target.value)} />
               <span className="text-[11.5px] text-muted-foreground">% recovered</span>
             </div>
           )}>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi testId="price-actual-per-cover" label="Actual cost a meal"
             value={money(data.per_cover.total, cur)}
             sub={`${money(data.per_cover.food, cur)} food · ${money(data.per_cover.labour, cur)} labour · ${money(data.per_cover.overheads, cur)} overheads`} />
        <Kpi testId="price-dishes-under" label="Dishes under their cost" accent="amber"
             value={data.dishes.filter((d) => d.verdict === "under the actual cost").length}
             sub={`of ${data.dishes.length} served`} />
        <Kpi testId="price-uplift" label="Would have recovered" accent="teal"
             value={money(data.uplift_over_period, cur, true)}
             sub="had these prices applied over the period" />
        <Kpi testId="price-covers" label="Meals priced on" value={data.covers}
             sub={`${shortDate(data.window.from)} to ${shortDate(data.window.to)}`} />
      </div>

      <div className="mt-4">
        <Table testId="price-advice-table"
               head={<><Th>Dish</Th><Th>Actual cost/head</Th><Th>Price now</Th>
                 <Th>Fair price</Th><Th>Change</Th><Th>Where it stands</Th></>}>
          {data.dishes.length === 0 && (
            <Empty label="Nothing has been served in this window yet" colSpan={6} />
          )}
          {data.dishes.map((d) => (
            <tr key={d.dish_ref} className="border-b border-border/60"
                data-testid={`price-dish-${d.dish_ref}`}>
              <Td>
                <span className="block text-[12.5px] text-foreground">{d.name}</span>
                <span className="block text-[10.5px] text-muted-foreground">
                  {d.times_served}× · {d.covers} meals · costed from the {d.costed_from}
                </span>
              </Td>
              <Td>
                <span className="block font-mono text-[12px] text-foreground"
                      data-testid={`price-actual-${d.dish_ref}`}>
                  {money(d.actual.total, cur)}
                </span>
                <span className="block font-mono text-[10px] text-muted-foreground">
                  {money(d.actual.food, cur)} + {money(d.actual.labour, cur)} +{" "}
                  {money(d.actual.overheads, cur)}
                </span>
              </Td>
              <Td className="font-mono text-[11px]" data-testid={`price-now-${d.dish_ref}`}>
                {money(d.price_now, cur)}
              </Td>
              <Td className="font-mono text-[12px] font-semibold text-foreground"
                  data-testid={`price-suggested-${d.dish_ref}`}>
                {money(d.suggested_price, cur)}
              </Td>
              <Td className="font-mono text-[11px]" data-testid={`price-change-${d.dish_ref}`}>
                {d.change > 0 ? `+${money(d.change, cur)}` : money(d.change, cur)}
              </Td>
              <Td>
                <Chip data-testid={`price-verdict-${d.dish_ref}`}
                      className={d.verdict === "under the actual cost"
                        ? "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300"
                        : d.verdict === "given free"
                          ? "border-border bg-muted text-muted-foreground"
                          : "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"}>
                  {d.verdict}
                </Chip>
              </Td>
            </tr>
          ))}
        </Table>
      </div>

      <ul className="mt-3 space-y-1" data-testid="price-notes">
        {data.notes.map((n) => (
          <li key={n} className="text-[11.5px] leading-relaxed text-muted-foreground">— {n}</li>
        ))}
      </ul>
    </Panel>
  );
};

const monthOptions = () => {
  const out = [];
  const now = new Date();
  for (let i = 0; i < 12; i += 1) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push([`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`,
      d.toLocaleString("en-GB", { month: "long", year: "numeric" })]);
  }
  return out;
};

const STATUS = {
  prepared: "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300",
  issued: "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
};

export const SafetyPacks = ({ canWrite }) => {
  const months = monthOptions();
  const [month, setMonth] = useState(months[0][0]);
  const [data, setData] = useState(null);
  const [prepare, setPrepare] = useState(null);
  const [sign, setSign] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const res = await api.get("/cafeteria/safety-packs");
      setData(res.data);
    } catch (e) { toast.error(apiError(e)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const pdfUrl = (ref) =>
    `${BACKEND_URL}/api/cafeteria/safety-packs/${ref}/pdf`;

  const submit = async (key, fn, message) => {
    setBusy(key);
    try {
      await fn();
      toast.success(message);
      await load();
      setPrepare(null);
      setSign(null);
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  if (!data) return <Loading label="Fetching the record packs" />;

  return (
    <div className="space-y-4">
      <Panel title="Inspector pack" testId="panel-safety-packs"
             kicker="The month's food-safety record as one document"
             actions={(
               <div className="flex flex-wrap items-center gap-2">
                 <Select value={month} onValueChange={setMonth}>
                   <SelectTrigger className="h-9 w-[170px]" data-testid="pack-month">
                     <SelectValue /></SelectTrigger>
                   <SelectContent>
                     {months.map(([key, label]) => (
                       <SelectItem key={key} value={key}>{label}</SelectItem>
                     ))}
                   </SelectContent>
                 </Select>
                 <a href={`${BACKEND_URL}/api/cafeteria/safety-pack.pdf?month=${month}`}
                    target="_blank" rel="noreferrer" data-testid="pack-print-unsigned"
                    className="inline-flex items-center rounded-full border border-border px-3.5 py-2 text-[12px] text-foreground transition-colors duration-150 hover:border-primary/40">
                   <Download className="mr-1.5 h-3.5 w-3.5" /> Unsigned record pack
                 </a>
                 {canWrite && (
                   <Button size="sm" data-testid="pack-prepare-button"
                           onClick={() => setPrepare({ month, declaration: "" })}
                           className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                     <FileCheck2 className="mr-1.5 h-3.5 w-3.5" /> Prepare a signed pack
                   </Button>
                 )}
               </div>
             )}>
        <p className="mb-3 text-[12px] text-muted-foreground">
          The unsigned pack prints straight away with signature lines. A signed pack is prepared by
          the kitchen and only issues once{" "}
          {(data.countersign_offices || []).join(" or ")} has countersigned it — that is the copy an
          inspector or auditor should be handed.
        </p>
        <Table testId="pack-table" head={<><Th>Reference</Th><Th>Month</Th><Th>Completeness</Th>
          <Th>Corrective actions</Th><Th>Status</Th><Th /></>}>
          {(data.items || []).length === 0 && <Empty label="No packs prepared yet" colSpan={6} />}
          {(data.items || []).map((p) => (
            <tr key={p.pack_ref} className="border-b border-border/60"
                data-testid={`pack-row-${p.pack_ref}`}>
              <Td className="font-mono text-[11px]">{p.pack_ref}</Td>
              <Td>{new Date(`${p.month}-01`).toLocaleString("en-GB",
                { month: "long", year: "numeric" })}</Td>
              <Td className="font-mono text-[11px]">
                {p.days_filed}/{p.days_expected}
                {p.missing_days > 0 && (
                  <span className="ml-1 text-amber-600 dark:text-amber-400">
                    {p.missing_days} missing
                  </span>
                )}
              </Td>
              <Td className="font-mono text-[11px]">{p.breaches}</Td>
              <Td><Chip className={STATUS[p.status] || ""}>{p.status}</Chip></Td>
              <Td className="text-right">
                <div className="flex justify-end gap-1.5">
                  {p.status === "prepared" && data.can_countersign && (
                    <Button size="sm" variant="outline" className="h-8 rounded-full"
                            data-testid={`pack-countersign-${p.pack_ref}`}
                            onClick={() => setSign({ ...p, minute: "" })}>
                      <PenLine className="mr-1.5 h-3.5 w-3.5" /> Countersign
                    </Button>
                  )}
                  {p.status === "issued" && (
                    <a href={pdfUrl(p.pack_ref)} target="_blank" rel="noreferrer"
                       data-testid={`pack-pdf-${p.pack_ref}`}
                       className="inline-flex h-8 items-center rounded-full border border-border px-3 text-[11.5px] text-foreground hover:border-primary/40">
                      <Download className="mr-1.5 h-3.5 w-3.5" /> Signed pack
                    </a>
                  )}
                </div>
              </Td>
            </tr>
          ))}
        </Table>
      </Panel>

      <Dialog open={Boolean(prepare)} onOpenChange={(o) => !o && setPrepare(null)}>
        <DialogContent data-testid="pack-prepare-dialog">
          <DialogHeader><DialogTitle>Prepare the pack</DialogTitle>
            <DialogDescription>
              The record is frozen as it stands today, including any days with no sheet.
            </DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-[11.5px] text-muted-foreground">Month</Label>
              <Input value={prepare?.month || ""} readOnly data-testid="pack-prepare-month" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[11.5px] text-muted-foreground">Declaration</Label>
              <Textarea rows={3} data-testid="pack-declaration"
                        placeholder="These records are a true copy of the kitchen's food-safety record for the month."
                        value={prepare?.declaration || ""}
                        onChange={(e) => setPrepare({ ...prepare, declaration: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="pack-prepare-save" disabled={busy === "prepare"}
                    onClick={() => submit("prepare", () => api.post("/cafeteria/safety-packs",
                      { month: prepare.month, declaration: prepare.declaration }),
                      "Pack prepared — it needs a countersignature before it issues")}
                    className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
              {busy === "prepare" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Prepare
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(sign)} onOpenChange={(o) => !o && setSign(null)}>
        <DialogContent data-testid="pack-countersign-dialog">
          <DialogHeader><DialogTitle>Countersign {sign?.pack_ref}</DialogTitle>
            <DialogDescription>
              Your signature issues the pack. The person who prepared it cannot countersign it.
            </DialogDescription></DialogHeader>
          <div className="space-y-1.5">
            <Label className="text-[11.5px] text-muted-foreground">Note (optional)</Label>
            <Textarea rows={3} data-testid="pack-countersign-minute" value={sign?.minute || ""}
                      onChange={(e) => setSign({ ...sign, minute: e.target.value })} />
          </div>
          <DialogFooter>
            <Button data-testid="pack-countersign-save" disabled={busy === "sign"}
                    onClick={() => submit("sign",
                      () => api.post(`/cafeteria/safety-packs/${sign.pack_ref}/countersign`,
                        { minute: sign.minute }), "Pack countersigned and issued")}
                    className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
              {busy === "sign" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Countersign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
